import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  EMPTY,
  filter,
  map,
  mergeMap,
  take,
} from 'rxjs';
import { OrderType } from 'src/app/api/contracts/order';
import { Product } from 'src/app/api/contracts/product';
import { CartService } from 'src/app/services/cart.service';
import { InStoreProductsService } from 'src/app/services/in-store-products.service';
import { ModalService } from 'src/app/services/modal.service';
import { ToastService } from 'src/app/services/toast.service';
import { ImageBreakpoint } from 'src/app/types/product-images.const';
import { LoaderController } from 'src/app/utils/loader-controller';

@Component({
  selector: 'app-product-search-modal',
  templateUrl: './product-search-modal.component.html',
  styleUrls: ['./product-search-modal.component.scss'],
})
export class ProductSearchModalComponent implements OnInit {
  @Input() locationHash: string = '';
  readonly searchControl = new FormControl<string>('');
  readonly foundProducts$ = new BehaviorSubject<Product[] | null>(null);
  readonly loader = new LoaderController();

  private readonly searchDebounceTime: number = 1000;

  public readonly smallImageBreakpoint = ImageBreakpoint.SMALL;

  constructor(
    public readonly modalService: ModalService,
    private readonly instoreService: InStoreProductsService,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly cartService: CartService
  ) {}

  ngOnInit() {
    this.subscribeForValueChange();
  }

  public foundProductsTrackByFn(index: number, product: Product): string {
    return `${product.id}`;
  }

  addProductToCart(product: Product): void {
    this.cartService.addItemToCart(
      product,
      OrderType.IN_STORE,
      null,
      null,
      null
    );
  }

  increaseProductAmount(productId: number): void {
    this.cartService.currentCart$
      .pipe(
        take(1),
        map((cart) => cart.items.find((i) => i.product.id === productId))
      )
      .subscribe((cartItem) => {
        cartItem && this.cartService.increaseAmount(cartItem);
      });
  }

  decreaseProductAmount(productId: number): void {
    this.cartService.currentCart$
      .pipe(
        take(1),
        map((cart) => cart.items.find((i) => i.product.id === productId))
      )
      .subscribe((cartItem) => {
        cartItem && this.cartService.decreaseAmount(cartItem);
      });
  }

  private subscribeForValueChange(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.searchDebounceTime),
        filter((value) => value?.length! > 2),
        mergeMap((value) => {
          this.loader.enqueue();
          return this.instoreService.findInStoreProductByName(
            value!,
            this.locationHash
          );
        }),
        catchError((err) => {
          this.loader.dequeue();
          this.toastService.showNotification({
            message: this.translateService.instant(
              '@components.productSearch.productList.errorMessage'
            ),
          });
          return EMPTY;
        })
      )
      .subscribe((productList) => {
        this.loader.dequeue();
        this.foundProducts$.next(productList || []);
      });
  }
}
